<template>
  <div class="live-tracking-page">
    <vue-element-loading :active="submiting" />
    <v-card class="rounded-lg elevation-1 bg-white">
      <!-- 1. PAGE HEADER -->
      <v-card-title class="d-flex justify-space-between align-center py-4 px-5 border-bottom">
        <div>
          <div class="d-flex align-center">
            <v-icon color="primary" class="me-2" size="28">mdi-radar</v-icon>
            <span class="text-h5 font-weight-black text-dark-primary">مركز التتبع المباشر</span>
            <v-chip
              small
              :color="staleWarnings.length > 0 ? 'amber lighten-4' : 'green lighten-5'"
              :class="staleWarnings.length > 0 ? 'amber--text text--darken-4 font-weight-bold ms-3' : 'green--text text--darken-3 font-weight-bold ms-3'"
            >
              <v-icon x-small left :color="staleWarnings.length > 0 ? 'amber darken-3' : 'green darken-2'">
                mdi-record-circle
              </v-icon>
              {{ staleWarnings.length > 0 ? 'تحديث متأخر' : 'مباشر' }}
            </v-chip>
          </div>
          <div class="text-caption text-dark-secondary mt-1">
            متابعة الرحلات الجارية والمركبات على الخريطة لحظياً بأحدث إشارات GPS
          </div>
        </div>

        <div class="d-flex align-center gap-2">
          <v-btn color="primary" small class="font-weight-bold white--text" :loading="submiting" @click="fetchOnRouteTrips">
            <v-icon left size="16">mdi-refresh</v-icon>
            تحديث الآن
          </v-btn>
        </div>
      </v-card-title>

      <!-- 2. GLOBAL PHASE 8 TRACKING WARNING BANNER -->
      <v-alert
        v-if="staleWarnings && staleWarnings.length > 0"
        type="warning"
        text
        dense
        dismissible
        class="mx-4 mt-3 mb-0 rounded-lg border-warning"
      >
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <v-icon color="amber darken-3" class="me-2">mdi-alert-outline</v-icon>
            <span class="font-weight-bold body-2 text-dark-primary">
              تنبيه تتبع نشط ({{ staleWarnings.length }}): {{ staleWarnings[0].message || 'مشكلة في اتصال GPS بإحدى الرحلات' }}
            </span>
          </div>
          <v-btn
            v-if="staleWarnings[0].planned_trip_id"
            x-small
            color="amber darken-3"
            class="white--text font-weight-bold ms-3"
            @click="selectTripById(staleWarnings[0].planned_trip_id)"
          >
            عرض الرحلة
          </v-btn>
        </div>
      </v-alert>

      <!-- OLD UNFINISHED TRIPS WARNING BANNER -->
      <v-alert
        v-if="oldUnfinishedTrips && oldUnfinishedTrips.length > 0"
        type="info"
        text
        dense
        dismissible
        class="mx-4 mt-2 mb-0 rounded-lg border-info"
      >
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <v-icon color="blue darken-2" class="me-2">mdi-history</v-icon>
            <span class="font-weight-bold body-2 text-dark-primary">
              تنبيه رحلات قديمة غير مغلقة: يوجد {{ oldUnfinishedTrips.length }} رحلة قديمة غير مغلقة في النظام
              (رحلة {{ oldUnfinishedTrips.map(t => '#' + t.id).join(', ') }}).
            </span>
          </div>
        </div>
      </v-alert>

      <!-- 3. TOP COMPACT KPI CARDS -->
      <div class="pa-4">
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 rounded-lg border-primary bg-light">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption font-weight-bold text-dark-secondary">الرحلات الجارية</div>
                  <div class="text-h5 font-weight-black text-dark-primary mt-1">{{ on_route_trips.length }}</div>
                </div>
                <v-avatar color="primary lighten-5" size="42">
                  <v-icon color="primary">mdi-bus</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 rounded-lg border-success bg-light">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption font-weight-bold text-dark-secondary">المركبات المتصلة (GPS)</div>
                  <div class="text-h5 font-weight-black green--text text--darken-3 mt-1">{{ connectedVehiclesCount }}</div>
                </div>
                <v-avatar color="green lighten-5" size="42">
                  <v-icon color="success">mdi-wifi</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 rounded-lg border-warning bg-light">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption font-weight-bold text-dark-secondary">GPS متأخر / بدون إشارة</div>
                  <div class="text-h5 font-weight-black warning--text text--darken-3 mt-1">{{ staleVehiclesCount }}</div>
                </div>
                <v-avatar color="amber lighten-5" size="42">
                  <v-icon color="warning darken-2">mdi-wifi-off</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 rounded-lg border-info bg-light">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-caption font-weight-bold text-dark-secondary">تنبيهات التتبع النشطة</div>
                  <div class="text-h5 font-weight-black blue--text text--darken-3 mt-1">{{ staleWarnings.length }}</div>
                </div>
                <v-avatar color="blue lighten-5" size="42">
                  <v-icon color="blue darken-2">mdi-bell-alert-outline</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- 4. TWO-COLUMN LAYOUT -->
        <v-row class="mt-2">
          <!-- RIGHT PANEL: LIVE TRIPS LIST (35% Width) -->
          <v-col cols="12" md="4" lg="4">
            <v-card outlined class="rounded-lg fill-height d-flex flex-column border-grey bg-white">
              <!-- Panel Header & Search -->
              <div class="pa-3 border-bottom grey lighten-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-subtitle-1 font-weight-bold text-dark-primary">
                    <v-icon size="20" color="primary" class="me-1">mdi-format-list-bulleted</v-icon>
                    الرحلات الجارية
                  </span>
                  <v-chip x-small color="primary" class="font-weight-bold white--text">
                    {{ filteredTrips.length }}
                  </v-chip>
                </div>

                <v-text-field
                  v-model="searchQuery"
                  placeholder="ابحث باسم السائق أو المسار أو رقم الرحلة..."
                  dense
                  outlined
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  class="bg-white text-dark-primary"
                ></v-text-field>

                <!-- Status Filter Chips -->
                <div class="d-flex align-center gap-1 mt-2 flex-wrap">
                  <v-chip
                    x-small
                    filter
                    :input-value="statusFilter === 'all'"
                    :color="statusFilter === 'all' ? 'primary' : 'grey lighten-3'"
                    :class="statusFilter === 'all' ? 'white--text font-weight-bold' : 'font-weight-bold'"
                    :style="statusFilter !== 'all' ? 'color: #111827 !important;' : ''"
                    @click="statusFilter = 'all'"
                  >
                    الكل
                  </v-chip>
                  <v-chip
                    x-small
                    filter
                    :input-value="statusFilter === 'healthy'"
                    :color="statusFilter === 'healthy' ? 'success' : 'grey lighten-3'"
                    :class="statusFilter === 'healthy' ? 'white--text font-weight-bold' : 'font-weight-bold'"
                    :style="statusFilter !== 'healthy' ? 'color: #111827 !important;' : ''"
                    @click="statusFilter = 'healthy'"
                  >
                    مباشر ({{ healthyTripsCount }})
                  </v-chip>
                  <v-chip
                    x-small
                    filter
                    :input-value="statusFilter === 'stale'"
                    :color="statusFilter === 'stale' ? 'warning' : 'grey lighten-3'"
                    :class="statusFilter === 'stale' ? 'white--text font-weight-bold' : 'font-weight-bold'"
                    :style="statusFilter !== 'stale' ? 'color: #111827 !important;' : ''"
                    @click="statusFilter = 'stale'"
                  >
                    متأخر ({{ staleVehiclesCount }})
                  </v-chip>
                  <v-chip
                    x-small
                    filter
                    :input-value="statusFilter === 'first_stop'"
                    :color="statusFilter === 'first_stop' ? 'purple darken-1' : 'grey lighten-3'"
                    :class="statusFilter === 'first_stop' ? 'white--text font-weight-bold' : 'font-weight-bold'"
                    :style="statusFilter !== 'first_stop' ? 'color: #111827 !important;' : ''"
                    @click="statusFilter = 'first_stop'"
                  >
                    عند أول محطة ({{ firstStopTripsCount }})
                  </v-chip>
                </div>
              </div>

              <!-- Trips Scrollable List -->
              <div class="flex-grow-1 overflow-y-auto pa-2" style="max-height: 520px;">
                <div v-if="filteredTrips.length === 0" class="text-center py-10 text-dark-secondary">
                  <v-icon size="60" color="grey lighten-1">mdi-bus-alert</v-icon>
                  <div class="mt-2 font-weight-bold text-body-2 text-dark-primary">لا توجد رحلات جارية مطابقة</div>
                </div>

                <v-card
                  v-else
                  v-for="(element, index) in filteredTrips"
                  :key="element.id"
                  outlined
                  class="pa-3 mb-2 rounded-lg cursor-pointer transition-all card-hover bg-white"
                  :class="selectedTrip && selectedTrip.id === element.id ? 'active-trip-card' : ''"
                  @click="selectTrip(element, index)"
                >
                  <div class="d-flex align-center justify-space-between mb-1">
                    <div class="d-flex align-center">
                      <v-avatar color="primary lighten-5" size="32" class="me-2">
                        <v-icon size="20" color="primary">mdi-account</v-icon>
                      </v-avatar>
                      <div>
                        <div class="font-weight-bold text-subtitle-2 text-dark-primary text-truncate">
                          {{ element.driver ? element.driver.name : 'سائق غير محدد' }}
                        </div>
                        <div class="text-caption text-dark-secondary font-weight-medium">رحلة #{{ element.id }}</div>
                      </div>
                    </div>

                    <v-chip
                      x-small
                      :color="element.is_tracking_stale ? 'warning' : 'success'"
                      class="font-weight-bold white--text"
                    >
                      {{ element.is_tracking_stale ? '⚠️ GPS متأخر' : '✓ مباشر' }}
                    </v-chip>
                  </div>

                  <div class="text-body-2 text-dark-primary font-weight-bold mt-1 d-flex align-center">
                    <v-icon size="16" color="primary" class="me-1">mdi-road-variant</v-icon>
                    {{ (element.trip && element.trip.route) ? element.trip.route.name : 'مسار غير محدد' }}
                  </div>

                  <div class="d-flex align-center justify-space-between text-caption text-dark-secondary mt-2 pt-2 border-top">
                    <div class="font-weight-medium text-dark-secondary">
                      <v-icon size="14" color="grey" class="me-1">mdi-clock-outline</v-icon>
                      بدأت: {{ element.started_at || element.movement_started_at || '—' }}
                    </div>
                    <div v-if="element.reservations_count !== undefined" class="font-weight-bold primary--text">
                      {{ element.reservations_count }} ركاب
                    </div>
                  </div>
                </v-card>
              </div>
            </v-card>
          </v-col>

          <!-- LEFT / MAIN PANEL: LIVE MAP & DETAILS (65% Width) -->
          <v-col cols="12" md="8" lg="8">
            <v-card outlined class="rounded-lg border-grey overflow-hidden bg-white">
              <!-- Map Control Bar -->
              <div class="pa-2 grey lighten-4 border-bottom d-flex align-center justify-space-between flex-wrap gap-2">
                <div class="d-flex align-center gap-2">
                  <v-btn small outlined color="primary" class="font-weight-bold text-dark-primary" @click="fitAllVehicles">
                    <v-icon left size="16">mdi-crop-free</v-icon>
                    إظهار جميع المركبات
                  </v-btn>
                  <v-btn v-if="selectedTrip" small outlined color="primary" class="font-weight-bold text-dark-primary" @click="centerSelectedVehicle">
                    <v-icon left size="16">mdi-crosshairs-gps</v-icon>
                    إعادة تمركز
                  </v-btn>
                </div>

                <div class="d-flex align-center gap-3">
                  <v-switch
                    v-model="autoFollow"
                    label="متابعة المركبة تلقائياً"
                    dense
                    hide-details
                    class="mt-0 font-weight-bold text-dark-primary"
                    color="primary"
                  ></v-switch>
                </div>
              </div>

              <!-- Google Map Container -->
              <div style="height: 480px; position: relative;" id="map-container">
                <GoogleMapLoader
                  :enabled="false"
                  :center="center"
                  :selected="selectedItem"
                  :zoom="zoom"
                  :apiKey="apiKey"
                  :markers="allMarkers"
                  :polylines="polyline"
                  :map-options="mapOptions"
                ></GoogleMapLoader>
              </div>
            </v-card>

            <!-- SELECTED TRIP DETAILS PANEL -->
            <v-card v-if="selectedTrip" outlined class="mt-3 rounded-lg border-primary bg-white">
              <v-card-title class="pa-3 primary lighten-5 border-bottom d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="me-2">mdi-bus-clock</v-icon>
                  <span class="text-subtitle-1 font-weight-bold text-dark-primary">
                    تفاصيل الرحلة المحددة #{{ selectedTrip.id }}
                  </span>
                  <v-chip
                    x-small
                    :color="selectedTrip.is_tracking_stale ? 'warning' : 'success'"
                    class="ms-2 font-weight-bold white--text"
                  >
                    {{ selectedTrip.is_tracking_stale ? '⚠️ GPS متأخر' : '✓ مباشر' }}
                  </v-chip>
                </div>

                <v-btn icon small color="grey" @click="deselectTrip">
                  <v-icon size="18">mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text class="pa-4">
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="text-caption text-dark-secondary font-weight-bold">السائق:</div>
                    <div class="text-body-2 font-weight-bold primary--text">
                      {{ selectedTrip.driver ? selectedTrip.driver.name : '—' }}
                    </div>
                    <div class="text-caption text-dark-secondary font-weight-medium" dir="ltr" style="text-align: right;">
                      {{ getDriverPhone(selectedTrip) }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <div class="text-caption text-dark-secondary font-weight-bold">المسار:</div>
                    <div class="text-body-2 font-weight-bold text-dark-primary">
                      {{ (selectedTrip.trip && selectedTrip.trip.route) ? selectedTrip.trip.route.name : '—' }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <div class="text-caption text-dark-secondary font-weight-bold">عدد الركاب:</div>
                    <div class="text-body-2 font-weight-bold green--text text--darken-3">
                      {{ selectedTrip.reservations_count !== undefined ? selectedTrip.reservations_count + ' ركاب' : '—' }}
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <div class="text-caption text-dark-secondary font-weight-bold">آخر تحديث للموقع:</div>
                    <div class="text-body-2 font-weight-bold text-dark-primary">
                      {{ selectedTrip.last_gps_update_at || selectedTrip.started_at || '—' }}
                    </div>
                  </v-col>
                </v-row>

                <!-- Action Buttons for Selected Trip -->
                <v-divider class="my-3"></v-divider>

                <div class="d-flex align-center flex-wrap gap-2">
                  <v-btn small color="primary" class="font-weight-bold white--text" @click="callDriver(selectedTrip)">
                    <v-icon left size="16">mdi-phone</v-icon>
                    الاتصال بالسائق
                  </v-btn>

                  <v-btn small outlined color="primary" class="font-weight-bold text-dark-primary" @click="openGpsHistoryModal">
                    <v-icon left size="16">mdi-history</v-icon>
                    سجل الحركة (GPS Logs)
                  </v-btn>

                  <v-btn small outlined color="grey darken-3" class="font-weight-bold text-dark-primary" @click="copyLocationLink(selectedTrip)">
                    <v-icon left size="16">mdi-content-copy</v-icon>
                    نسخ الإحداثيات
                  </v-btn>

                  <v-spacer></v-spacer>

                  <v-btn small text color="error" class="font-weight-bold" @click="deselectTrip">
                    <v-icon left size="16">mdi-eye-off</v-icon>
                    إيقاف متابعة الرحلة على الخريطة
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <!-- GPS History Modal -->
    <gps-history-modal
      v-model="showHistoryModal"
      :planned-trip-id="selectedTrip ? selectedTrip.id : null"
    ></gps-history-modal>
  </div>
</template>

<script>
import GoogleMapLoader, { BEEPGO_MAP_STYLE } from "../../components/GoogleMapLoader.vue";
import VueElementLoading from "vue-element-loading";
import GpsHistoryModal from "./GpsHistoryModal.vue";
import { Keys } from "/src/config.js";

export default {
  components: {
    GoogleMapLoader,
    VueElementLoading,
    GpsHistoryModal,
  },
  data() {
    return {
      apiKey: Keys.GOOGLE_MAPS_API_KEY,
      mapOptions: {
        styles: BEEPGO_MAP_STYLE,
        disableDefaultUI: false,
        zoomControl: true,
        fullscreenControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        scaleControl: true,
      },
      markers: [],
      stopMarkers: [],
      selectedIdx: null,
      selectedTrip: null,
      on_route_trips: [],
      staleWarnings: [],
      polyline: [],
      center: {
        lat: parseFloat(Keys.VUE_APP_ORIGIN_LAT) || 30.0444,
        lng: parseFloat(Keys.VUE_APP_ORIGIN_LNG) || 31.2357,
      },
      zoom: 12,
      selectedItem: null,
      submiting: false,
      searchQuery: "",
      statusFilter: "all",
      autoFollow: true,
      showHistoryModal: false,
    };
  },
  computed: {
    hasLocation() {
      return (trip) => {
        return (
          trip &&
          trip.last_position_lat !== null &&
          trip.last_position_lat !== undefined &&
          trip.last_position_lng !== null &&
          trip.last_position_lng !== undefined &&
          String(trip.last_position_lat).trim() !== "" &&
          String(trip.last_position_lng).trim() !== ""
        );
      };
    },
    connectedVehiclesCount() {
      return this.on_route_trips.filter(t => this.hasLocation(t) && !t.is_tracking_stale).length;
    },
    staleVehiclesCount() {
      return this.on_route_trips.filter(t => !this.hasLocation(t) || t.is_tracking_stale).length;
    },
    healthyTripsCount() {
      return this.connectedVehiclesCount;
    },
    firstStopTripsCount() {
      return this.on_route_trips.filter(t => {
        const seq = t.current_stop_sequence ?? t.last_completed_stop_index ?? 0;
        return seq <= 1;
      }).length;
    },
    oldUnfinishedTrips() {
      const todayCairoStr = new Date().toISOString().split("T")[0];
      return this.on_route_trips.filter(trip => {
        if (!trip.planned_date) return false;
        return trip.planned_date < todayCairoStr;
      });
    },
    filteredTrips() {
      return this.on_route_trips.filter(trip => {
        if (this.statusFilter === "healthy" && (!this.hasLocation(trip) || trip.is_tracking_stale)) return false;
        if (this.statusFilter === "stale" && (this.hasLocation(trip) && !trip.is_tracking_stale)) return false;
        if (this.statusFilter === "first_stop") {
          const seq = trip.current_stop_sequence ?? trip.last_completed_stop_index ?? 0;
          if (seq > 1) return false;
        }

        if (this.searchQuery && this.searchQuery.trim() !== "") {
          const q = this.searchQuery.trim().toLowerCase();
          const driverName = trip.driver ? (trip.driver.name || "").toLowerCase() : "";
          const driverPhone = trip.driver ? (trip.driver.phone_number || trip.driver.tel_number || "").toLowerCase() : "";
          const routeName = (trip.trip && trip.trip.route) ? trip.trip.route.name.toLowerCase() : "";
          const tripId = String(trip.id);

          return driverName.includes(q) || driverPhone.includes(q) || routeName.includes(q) || tripId.includes(q);
        }

        return true;
      });
    },
    allMarkers() {
      return [...this.markers, ...this.stopMarkers];
    },
  },
  mounted() {
    this.center.lat = parseFloat(this.center.lat);
    this.center.lng = parseFloat(this.center.lng);
    this.fetchOnRouteTrips();
  },
  beforeDestroy() {
    this.stopListeningToChannels();
  },
  methods: {
    fetchOnRouteTrips() {
      this.submiting = true;
      axios
        .get("/planned-trips/on-route")
        .then((response) => {
          this.submiting = false;
          this.on_route_trips = response.data.running || [];
          this.staleWarnings = response.data.stale_warnings || [];

          this.markers = [];
          if (this.on_route_trips.length > 0) {
            for (let i = 0; i < this.on_route_trips.length; i++) {
              this.addBusIcon(this.on_route_trips[i]);
            }
            this.listenToChannels();
          }
        })
        .catch((error) => {
          this.submiting = false;
          console.error("Error fetching on-route trips:", error);
          this.$notify({
            title: "Error",
            text: "تعذر تحميل الرحلات الجارية على الخريطة",
            type: "error",
          });
        });
    },
    createVehicleSvgIcon(tripId, isStale, isSelected) {
      const size = isSelected ? 46 : 36;
      const height = Math.round(size * 1.25);
      const strokeColor = isSelected ? (isStale ? "#D97706" : "#082C69") : "#ffffff";
      const strokeWidth = isSelected ? 3 : 2.5;
      const fill = isStale ? "#D97706" : "#059669";
      const tripNum = String(tripId).slice(-4);

      // Official Material Design mdi-bus path
      const busPath =
        "M18,11H6V6H18M16.5,17A1.5,1.5 0 0,1 15,15.5A1.5,1.5 0 0,1 16.5,14A1.5,1.5 0 0,1 18,15.5A1.5,1.5 0 0,1 16.5,17M7.5,17A1.5,1.5 0 0,1 6,15.5A1.5,1.5 0 0,1 7.5,14A1.5,1.5 0 0,1 9,15.5A1.5,1.5 0 0,1 7.5,17M4,16C4,16.88 4.39,17.67 5,18.22V20A1,1 0 0,0 6,21H7A1,1 0 0,0 8,20V19H16V20A1,1 0 0,0 17,21H18A1,1 0 0,0 19,20V18.22C19.61,17.67 20,16.88 20,16V6C20,2.5 16.42,2 12,2C7.58,2 4,2.5 4,6V16Z";

      const selectionRing = isSelected
        ? `<circle cx="20" cy="20" r="19" fill="none" stroke="${isStale ? '#F59E0B' : '#3B82F6'}" stroke-width="2" opacity="0.75"/>`
        : "";

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${height}" viewBox="0 0 40 50">
        ${selectionRing}
        <circle cx="20" cy="20" r="16.5" fill="${fill}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>
        <path d="${busPath}" fill="#ffffff" transform="translate(8, 8.5)"/>
        <rect x="4" y="36" width="32" height="12" rx="3" fill="#0F172A" opacity="0.92"/>
        <text x="20" y="45" font-size="9" font-weight="bold" fill="#ffffff" text-anchor="middle">#${tripNum}</text>
      </svg>`;

      return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize:
          typeof google !== "undefined" && google.maps && google.maps.Size
            ? new google.maps.Size(size, height)
            : { width: size, height },
        anchor:
          typeof google !== "undefined" && google.maps && google.maps.Point
            ? new google.maps.Point(size / 2, size / 2)
            : { x: size / 2, y: size / 2 },
      };
    },
    createStopSvgIcon(stopNumber, isStart, isFinal) {
      let bg = "#673ab7";
      if (isStart) bg = "#2e7d32";
      if (isFinal) bg = "#c62828";

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
        <circle cx="15" cy="15" r="13" fill="${bg}" stroke="#ffffff" stroke-width="2"/>
        <text x="15" y="19" font-size="12" font-weight="bold" fill="#ffffff" text-anchor="middle">${stopNumber}</text>
      </svg>`;

      return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: { width: 30, height: 30 },
        anchor: { x: 15, y: 15 }
      };
    },
    addBusIcon(on_route_trip) {
      if (!this.hasLocation(on_route_trip)) return;

      const position = {
        lat: parseFloat(on_route_trip.last_position_lat),
        lng: parseFloat(on_route_trip.last_position_lng),
      };
      let infoText = this.getTripInfoText(on_route_trip);

      const isSelected = this.selectedTrip && this.selectedTrip.id === on_route_trip.id;
      const icon = this.createVehicleSvgIcon(on_route_trip.id, on_route_trip.is_tracking_stale, isSelected);

      let marker = {
        place_id: on_route_trip.channel,
        position: position,
        infoText: infoText,
        icon: icon,
        zIndex: isSelected ? 9999 : 10,
      };
      this.markers.push(marker);
    },
    getTripInfoText(on_route_trip, speed = null) {
      let infoText = "";
      if (on_route_trip.driver) {
        infoText += "<b>Driver:</b> " + on_route_trip.driver.name + "<br/>";
      }
      if (on_route_trip.trip && on_route_trip.trip.route) {
        infoText += "<b>Route:</b> " + on_route_trip.trip.route.name + "<br/>";
      }
      if (speed) {
        infoText += "<b>Speed:</b> " + speed + " km/h<br/>";
      }
      return infoText;
    },
    selectTrip(trip, index) {
      this.selectedTrip = trip;
      this.selectedIdx = index;
      this.selectedItem = trip.channel;

      this.updateMarkerVisualState();

      if (this.hasLocation(trip)) {
        this.center = {
          lat: parseFloat(trip.last_position_lat),
          lng: parseFloat(trip.last_position_lng),
        };
      }

      this.loadSelectedTripRouteVisuals(trip);
    },
    updateMarkerVisualState() {
      this.markers.forEach((m, idx) => {
        const trip = this.on_route_trips[idx];
        if (trip) {
          const isSelected = this.selectedTrip && this.selectedTrip.id === trip.id;
          m.icon = this.createVehicleSvgIcon(trip.id, trip.is_tracking_stale, isSelected);
          m.zIndex = isSelected ? 9999 : 10;
        }
      });
    },
    loadSelectedTripRouteVisuals(trip) {
      this.polyline = [];
      this.stopMarkers = [];

      if (!trip || !trip.trip || !trip.trip.route) return;

      const routeStops = trip.trip.route.route_stops || trip.trip.route.stops || [];
      if (routeStops.length > 0) {
        const pathData = [];
        routeStops.forEach((rs, idx) => {
          const stopObj = rs.stop || rs;
          if (stopObj && stopObj.lat && stopObj.lng) {
            const pos = { lat: parseFloat(stopObj.lat), lng: parseFloat(stopObj.lng) };
            pathData.push(pos);

            const isStart = idx === 0;
            const isFinal = idx === routeStops.length - 1;
            const stopIcon = this.createStopSvgIcon(idx + 1, isStart, isFinal);

            this.stopMarkers.push({
              place_id: `stop-${idx + 1}`,
              position: pos,
              infoText: `<b>المحطة ${idx + 1}:</b> ${stopObj.name || 'محطة'}`,
              icon: stopIcon,
              zIndex: 5,
            });
          }
        });

        if (pathData.length > 0) {
          this.polyline = [{
            data: pathData,
            strokeColor: "#673ab7",
          }];
        }
      }
    },
    selectTripById(tripId) {
      const foundIdx = this.on_route_trips.findIndex(t => t.id === tripId);
      if (foundIdx !== -1) {
        this.selectTrip(this.on_route_trips[foundIdx], foundIdx);
      }
    },
    deselectTrip() {
      this.selectedTrip = null;
      this.selectedIdx = null;
      this.selectedItem = null;
      this.polyline = [];
      this.stopMarkers = [];
      this.updateMarkerVisualState();
    },
    fitAllVehicles() {
      if (this.on_route_trips.length === 0) return;
      let validTrips = this.on_route_trips.filter(t => this.hasLocation(t));
      if (validTrips.length > 0) {
        this.center = {
          lat: parseFloat(validTrips[0].last_position_lat),
          lng: parseFloat(validTrips[0].last_position_lng),
        };
        this.zoom = 11;
      }
    },
    centerSelectedVehicle() {
      if (this.selectedTrip && this.hasLocation(this.selectedTrip)) {
        this.center = {
          lat: parseFloat(this.selectedTrip.last_position_lat),
          lng: parseFloat(this.selectedTrip.last_position_lng),
        };
        this.zoom = 14;
      }
    },
    getDriverPhone(trip) {
      if (!trip || !trip.driver) return "—";
      return trip.driver.phone_number || trip.driver.tel_number || "—";
    },
    callDriver(trip) {
      const phone = this.getDriverPhone(trip);
      if (phone && phone !== "—") {
        window.location.href = `tel:${phone}`;
      } else {
        this.$notify({ title: "ملاحظة", text: "رقم هاتف السائق غير متوفر", type: "info" });
      }
    },
    copyLocationLink(trip) {
      if (!trip || !this.hasLocation(trip)) {
        this.$notify({ title: "خطأ", text: "إحداثيات الموقع غير متوفرة", type: "error" });
        return;
      }
      const coords = `${trip.last_position_lat},${trip.last_position_lng}`;
      const url = `https://maps.google.com/?q=${coords}`;
      navigator.clipboard.writeText(url).then(() => {
        this.$notify({ title: "نجاح", text: "تم نسخ رابط الموقع للحافظة بنجاح", type: "success" });
      });
    },
    openGpsHistoryModal() {
      this.showHistoryModal = true;
    },
    stopListeningToChannels() {
      if (window.Echo && this.on_route_trips) {
        for (let i = 0; i < this.on_route_trips.length; i++) {
          if (this.on_route_trips[i].channel) {
            window.Echo.leave(this.on_route_trips[i].channel);
          }
        }
      }
    },
    listenToChannels() {
      this.stopListeningToChannels();
      for (let index = 0; index < this.on_route_trips.length; index++) {
        this.listenToChannel(index, this.on_route_trips[index]);
      }
    },
    animateMarkerPosition(marker, newLat, newLng, speed, trip) {
      if (!marker || !marker.position) return;

      const oldLat = marker.position.lat;
      const oldLng = marker.position.lng;

      const latDiff = Math.abs(newLat - oldLat);
      const lngDiff = Math.abs(newLng - oldLng);
      if (latDiff > 0.05 || lngDiff > 0.05 || (oldLat === 0 && oldLng === 0)) {
        marker.position = { lat: newLat, lng: newLng };
        marker.infoText = this.getTripInfoText(trip, speed);
        return;
      }

      if (marker.animFrameId) {
        cancelAnimationFrame(marker.animFrameId);
        marker.animFrameId = null;
      }

      let startTime = null;
      const duration = 2000;

      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1.0);

        const currentLat = oldLat + (newLat - oldLat) * progress;
        const currentLng = oldLng + (newLng - oldLng) * progress;

        marker.position = { lat: currentLat, lng: currentLng };
        marker.infoText = this.getTripInfoText(trip, speed);

        if (progress < 1.0) {
          marker.animFrameId = requestAnimationFrame(step);
        } else {
          marker.position = { lat: newLat, lng: newLng };
          marker.animFrameId = null;
        }
      };

      marker.animFrameId = requestAnimationFrame(step);
    },
    listenToChannel(index, trip) {
      if (!window.Echo) return;
      window.Echo.channel(trip.channel).listen("TripPositionUpdated", (e) => {
        let data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        let lat = parseFloat(data.lat);
        let lng = parseFloat(data.lng);
        let speed = data.speed ? Math.round(data.speed * 100) / 100 : null;

        trip.last_position_lat = lat;
        trip.last_position_lng = lng;

        let marker = this.markers[index];
        if (marker) {
          this.animateMarkerPosition(marker, lat, lng, speed, trip);
        }

        if (this.selectedItem === trip.channel && this.autoFollow) {
          this.center = { lat, lng };
        }
      });
    },
  },
};
</script>

<style scoped>
/* High Contrast Scoped Live Tracking Styles */
.live-tracking-page {
  color: #111827 !important;
}

.text-dark-primary {
  color: #111827 !important;
}

.text-dark-secondary {
  color: #374151 !important;
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.border-bottom { border-bottom: 1px solid #e5e7eb !important; }
.border-top { border-top: 1px solid #f3f4f6 !important; }
.border-grey { border: 1px solid #e5e7eb !important; }
.border-primary { border-top: 3px solid #673ab7 !important; }
.border-success { border-top: 3px solid #2e7d32 !important; }
.border-warning { border-top: 3px solid #ef6c00 !important; }
.border-info { border-top: 3px solid #0288d1 !important; }
.bg-light { background-color: #f9fafb !important; }
.card-hover:hover { background-color: #f3edfc !important; }
.active-trip-card {
  border: 2px solid #673ab7 !important;
  background-color: #f3edfc !important;
}
.cursor-pointer { cursor: pointer; }
</style>
